import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL } from '../../../apiConfig';
import { motion } from 'framer-motion';
import useBreakpoint from '../../../hooks/useBreakpoint';
import NoticeModal from './NoticeModal';

// Initial data using requested images in case backend is empty or unreachable
const fallbackNotices = [
    {
        id: 1,
        title: "BTCL 2026 Season – Team Registration Now Open",
        image: "not01.jpg",
        link: "#",
        isLarge: true
    },
    {
        id: 2,
        title: "Last Date Extended for Club & Player Registrations",
        image: "not02.jpg",
        link: "#",
        isLarge: false
    },
    {
        id: 3,
        title: "Important Notice: League Fixtures & Division Details to Be Published Soon",
        image: "not03.jpg",
        link: "#",
        isLarge: false
    }
];

const SpecialNotices = () => {
    const navigate = useNavigate();
    const { isMobile, isDesktop } = useBreakpoint();
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [isNoticeModalOpen, setIsNoticeModalOpen] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const language = document.getElementById('language')?.value || '1';

        // Fetch from new backend endpoint
        axios.get(`${API_BASE_URL}/specialNotices/${language}`)
            .then(res => {
                if (isMounted) {
                    const backendNotices = res.data.special_notices || [];
                    if (backendNotices.length > 0) {
                        // Use backend notices, and fill remaining slots with fallbacks if < 3
                        const combinedNotices = [...backendNotices];
                        if (combinedNotices.length < 3) {
                            combinedNotices.push(...fallbackNotices.slice(combinedNotices.length));
                        }
                        setNotices(combinedNotices.slice(0, 3));
                    } else {
                        setNotices(fallbackNotices);
                    }
                    setLoading(false);
                }
            })
            .catch(err => {
                console.error("SpecialNotices fetch error:", err);
                if (isMounted) {
                    setNotices(fallbackNotices);
                    setLoading(false);
                }
            });

        return () => { isMounted = false; };
    }, []);

    const handleNoticeClick = (notice) => {
        setSelectedNotice(notice);
        setIsNoticeModalOpen(true);
    };

    if (loading) return null;

    // Helper to get image URL with fallback support
    const getImageUrl = (imageName, fallbackIdx = 0) => {
        // Fallback sequence using existing local images
        const defaultImages = ['not01.jpg', 'not02.jpg', 'not03.jpg'];
        const fallback = defaultImages[fallbackIdx % defaultImages.length];

        if (!imageName) {
            try {
                return require(`../../../images/${fallback}`);
            } catch (e) {
                return '';
            }
        }

        if (imageName.startsWith('not')) {
            // These are the local images in the images folder
            try {
                return require(`../../../images/${imageName}`);
            } catch (e) {
                return `${FILE_BASE_URL}/${imageName}`;
            }
        }
        return `${FILE_BASE_URL}/${imageName}`;
    };

    const bigNotice = notices[0] || fallbackNotices[0];
    const smallNotices = notices.slice(1, 3);
    if (smallNotices.length < 2) {
        smallNotices.push(...fallbackNotices.slice(notices.length).slice(0, 2 - smallNotices.length));
    }

    return (
        <section style={{ 
            width: '100%', 
            maxWidth: 'var(--maxWidth)', 
            margin: '0 auto 60px auto', 
            padding: !isDesktop ? '0 16px' : '0 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxSizing: 'border-box'
        }}>
            {/* Section Title */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '25px' }}>
                <h2 style={{
                    fontFamily: 'var(--fontDisplay)',
                    fontSize: isMobile ? '22px' : '28px',
                    fontWeight: 800,
                    lineHeight: '1.2',
                    margin: 0,
                    color: '#000',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                }}>
                    Special Notices
                </h2>

                <div 
                    onClick={() => navigate('/notices-list')}
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer' 
                    }}
                >
                    <span style={{ 
                        fontFamily: 'var(--fontRajdhani)', 
                        fontWeight: 600, 
                        fontSize: '16px', 
                        lineHeight: '20px', 
                        textDecoration: 'underline', 
                        color: 'var(--blue)' 
                    }}>
                        View More
                    </span>
                </div>
            </div>

            {/* Bento Grid Content */}
            <div className="special-notices-grid" style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch', // Changed to stretch to ensure equal height
                gap: !isDesktop ? '24px' : '32px',
                width: '100%',
                height: isMobile ? 'auto' : '540px', // Standardized height
                boxSizing: 'border-box'
            }}>
                
                {/* Left Div (Big) */}
                <motion.div 
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    style={{
                        flex: 1,
                        backgroundImage: `url("${getImageUrl(bigNotice.image, 0)}")`,
                        backgroundPosition: 'center',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        padding: '24px',
                        gap: '24px',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        boxSizing: 'border-box'
                    }}
                >
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)', // Strengthened bottom gradient
                        zIndex: 0
                    }} />
                    
                    <h3 style={{
                        fontFamily: 'var(--fontAnton)',
                        fontWeight: 400,
                        fontSize: !isDesktop ? '32px' : '48px',
                        lineHeight: '1.1',
                        textTransform: 'uppercase',
                        color: '#FFFFFF',
                        margin: 0,
                        zIndex: 1,
                        maxWidth: !isDesktop ? '100%' : '552px',
                        textShadow: '0 2px 10px rgba(0,0,0,0.5)' // Added text shadow for readability
                    }}>
                        {bigNotice.title}
                    </h3>

                    <motion.button 
                        onClick={() => handleNoticeClick(bigNotice)}
                        style={{
                            width: '150px',
                            height: '44px',
                            background: 'var(--blue)',
                            color: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            fontFamily: 'var(--fontRajdhani)',
                            fontWeight: 700,
                            fontSize: '16px',
                            borderRadius: '0px',
                            zIndex: 2,
                            transition: 'transform 0.2s',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.3)' // Added depth to button
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Learn More
                    </motion.button>
                </motion.div>

                {/* Right Div (Stacked Small) */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px', // Reduced gap for tighter fit
                }}>
                    {smallNotices.map((notice, idx) => (
                        <motion.div 
                            key={notice.id || idx}
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.2 }}
                            style={{
                                flex: 1,
                                backgroundImage: `url("${getImageUrl(notice.image, idx + 1)}")`,
                                backgroundPosition: 'center',
                                backgroundSize: 'cover',
                                backgroundRepeat: 'no-repeat',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                                padding: '24px',
                                gap: '24px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                boxSizing: 'border-box'
                            }}
                        >
                             <div style={{
                                 position: 'absolute',
                                 inset: 0,
                                 background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)', // Strengthened bottom gradient
                                 zIndex: 0
                             }} />

                             <h4 style={{
                                 fontFamily: 'var(--fontRajdhani)',
                                 fontWeight: 700,
                                 fontSize: '24px',
                                 lineHeight: '1.3',
                                 color: '#FFFFFF',
                                 margin: 0,
                                 zIndex: 1,
                                 maxWidth: !isDesktop ? '100%' : '552px',
                                 textShadow: '0 2px 8px rgba(0,0,0,0.5)' // Added text shadow for readability
                             }}>
                                 {notice.title}
                             </h4>

                             <motion.button 
                                onClick={() => handleNoticeClick(notice)}
                                style={{
                                    width: '150px', // Standardized width
                                    height: '40px', // Standardized height
                                    background: 'var(--blue)',
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: 'none',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    fontFamily: 'var(--fontRajdhani)',
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    borderRadius: '0px',
                                    zIndex: 2,
                                    transition: 'transform 0.2s',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)' // Added depth
                                }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Learn More
                            </motion.button>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Responsive styles */}
            <style>{`
                @media (max-width: 1024px) {
                    .special-notices-grid {
                        flex-direction: column !important;
                        height: auto !important;
                    }
                    .special-notices-grid > div {
                        width: 100% !important;
                        flex: none !important;
                    }
                }
                @media (max-width: 768px) {
                    .special-notices-grid h3 {
                        fontSize: 40px !important;
                    }
                }
            `}</style>
            <NoticeModal 
                notice={selectedNotice} 
                isOpen={isNoticeModalOpen} 
                onClose={() => setIsNoticeModalOpen(false)} 
            />
        </section>
    );
};

export default SpecialNotices;
